import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../game_area/header';

// Pet Runner is embedded inside the React shell at /play/pet-runner. In dev
// the iframe inside that route hits /pet-runner/ which is proxied to the
// Vite dev server by setupProxy.js. In production the same path is served
// by Express. Either way the player stays inside the same SPA.

function GameCard({ title, subtitle, accent, badge, onPlay, comingSoon, image }) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            disabled={comingSoon}
            onClick={onPlay}
            className={`group relative neon-panel overflow-hidden text-left transition-all duration-200 ${
                comingSoon
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:scale-[1.02] hover:shadow-neon-strong cursor-pointer'
            }`}
        >
            <div
                className="aspect-[16/10] w-full flex items-center justify-center relative"
                style={{
                    background:
                        accent ||
                        'linear-gradient(135deg, rgba(34, 211, 238, 0.18), rgba(168, 85, 247, 0.18))',
                }}
            >
                {image && (
                    <span
                        className="text-7xl sm:text-8xl drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
                        role="img"
                        aria-hidden="true"
                    >
                        {image}
                    </span>
                )}
                {badge && (
                    <span className="absolute top-2 left-2 text-[10px] tracking-widest font-display px-2 py-0.5 rounded-md bg-black/50 border border-cyber-cyan/60 text-cyber-cyan">
                        {badge}
                    </span>
                )}
                {comingSoon && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[1px]">
                        <span className="text-[10px] tracking-widest font-display text-cyber-dim">
                            {t('games.comingSoon', 'Coming soon')}
                        </span>
                    </span>
                )}
            </div>
            <div className="px-4 py-3 border-t border-cyber-border/40">
                <div className="font-display text-base sm:text-lg text-white tracking-wide">
                    {title}
                </div>
                <div className="text-[11px] sm:text-xs text-cyber-dim mt-1 leading-relaxed">
                    {subtitle}
                </div>
            </div>
            {!comingSoon && (
                <div className="absolute bottom-3 right-3 text-[10px] tracking-widest font-display px-2 py-1 rounded-md bg-cyber-cyan/15 border border-cyber-cyan/60 text-cyber-cyan opacity-0 group-hover:opacity-100 transition">
                    {t('games.play', 'Play →')}
                </div>
            )}
        </button>
    );
}

export default function GamesList({ player, online, onLogout }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (!player?.name) navigate('/');
    }, [player, navigate]);

    const goToLobby = (gameType) => {
        // Hand the lobby a hint about which tab to open. GameArea reads
        // localStorage('lobby.preferredType') in its initial state.
        try {
            localStorage.setItem('lobby.preferredType', gameType);
        } catch {
            /* ignore quota errors */
        }
        navigate('/game');
    };

    const openPetRunner = () => {
        navigate('/play/pet-runner');
    };

    const games = [
        {
            id: 'coinflip',
            title: t('games.coinflip.title', 'Coin Flip'),
            subtitle: t(
                'games.coinflip.subtitle',
                '1v1 PvP — pick a side and let the coin decide. Provably fair.',
            ),
            badge: 'PvP',
            image: '🪙',
            accent:
                'linear-gradient(135deg, rgba(250, 204, 21, 0.22), rgba(168, 85, 247, 0.22))',
            onPlay: () => goToLobby('coinflip'),
        },
        {
            id: 'battle',
            title: t('games.battle.title', 'Battle'),
            subtitle: t(
                'games.battle.subtitle',
                'HP duel — attack and block across three zones. First to 0 HP loses.',
            ),
            badge: 'PvP',
            image: '⚔️',
            accent:
                'linear-gradient(135deg, rgba(244, 63, 94, 0.22), rgba(99, 102, 241, 0.22))',
            onPlay: () => goToLobby('battle'),
        },
        {
            id: 'blackjack',
            title: t('games.blackjack.title', 'Blackjack'),
            subtitle: t(
                'games.blackjack.subtitle',
                'Beat the dealer to 21 — hit, stand, double. Stake the room bet.',
            ),
            badge: 'vs House',
            image: '🃏',
            accent:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.22), rgba(99, 102, 241, 0.22))',
            onPlay: () => goToLobby('blackjack'),
        },
        {
            id: 'pet-runner',
            title: t('games.petRunner.title', 'Pet Runner'),
            subtitle: t(
                'games.petRunner.subtitle',
                'Pick a pet and collect coins on a dungeon map. Solo, 60-second runs.',
            ),
            badge: 'Solo',
            image: '🦊',
            accent:
                'linear-gradient(135deg, rgba(34, 211, 238, 0.22), rgba(244, 114, 182, 0.22))',
            onPlay: openPetRunner,
        },
    ];

    return (
        <div className="min-h-screen relative bg-cyber-bg">
            <Header player={player || {}} online={online || 0} onLogout={onLogout} />

            <main className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
                <div className="mb-6 sm:mb-10">
                    <h1 className="text-2xl sm:text-4xl font-display font-black text-white tracking-wide">
                        {t('games.title', 'Choose a game')}
                    </h1>
                    <p className="text-cyber-dim text-xs sm:text-sm mt-2 max-w-2xl">
                        {t(
                            'games.subtitle',
                            'PvP rooms share your wallet balance. Solo games run separately and are free to play.',
                        )}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {games.map((g) => (
                        <GameCard key={g.id} {...g} />
                    ))}
                </div>
            </main>
        </div>
    );
}
